/*
 * Cart drawer add-ons: threshold-based free gift and complementary-product upsell.
 *
 * Both custom elements live inside `.drawer__inner`, which the drawer replaces
 * wholesale on every cart mutation. That makes `connectedCallback` the natural
 * place to re-evaluate state — the elements are re-created with fresh Liquid data
 * each time, so they never have to track the cart themselves.
 */
(function () {
  'use strict';

  var GIFT_DECLINED_KEY = 'cart-drawer:gift-declined';

  /* Guards against a re-render racing an in-flight mutation and adding the gift twice. */
  var busy = false;

  /*
   * The refresh swaps in a fresh <cart-gift> while the lock is still held, so that
   * element skips its own evaluation. Re-run it once the lock is released.
   */
  function releaseGiftLock() {
    busy = false;
    var gift = document.querySelector('cart-gift');
    if (gift && typeof gift.evaluate === 'function') gift.evaluate();
  }

  /* Recommendations are identical for an unchanged cart, so don't refetch per re-render. */
  var upsellCache = new Map();

  function safeSession(fn, fallback) {
    try {
      return fn();
    } catch (e) {
      return fallback;
    }
  }

  /*
   * No `sections` in the payload on purpose: on this store the cart mutation answers
   * with `sections: null`. refreshCartDrawer() (cart-drawer.js) re-renders through the
   * GET section route instead, which does return the sections.
   */
  function cartRequest(url, payload) {
    return fetch(url, Object.assign({}, fetchConfig(), { body: JSON.stringify(payload) }))
      .then(function (response) {
        return response.json();
      })
      .then(function (state) {
        if (state.status) {
          /* Shopify returns {status, message, description} for rejected mutations. */
          console.warn('[cart-drawer]', state.description || state.message);
          return state;
        }
        if (typeof window.refreshCartDrawer !== 'function') return state;
        return window.refreshCartDrawer().then(function () {
          return state;
        });
      });
  }

  /* ----------------------------------------------------------------- free gift */

  class CartGift extends HTMLElement {
    connectedCallback() {
      var declineButton = this.querySelector('[data-cart-gift-decline]');
      if (declineButton) {
        declineButton.addEventListener('click', this.decline.bind(this));
      }

      this.evaluate();
    }

    evaluate() {
      /* A re-render during an in-flight mutation would otherwise add the gift twice. */
      if (busy) return;

      var threshold = parseInt(this.dataset.threshold, 10);
      var qualifying = parseInt(this.dataset.qualifying, 10);
      var variantId = parseInt(this.dataset.variantId, 10);
      var inCart = this.dataset.inCart === 'true';

      if (!threshold || !variantId) return;

      if (qualifying < threshold) {
        /* Below the threshold again — the offer resets, including a previous decline. */
        safeSession(function () {
          return sessionStorage.removeItem(GIFT_DECLINED_KEY);
        });
        if (inCart) this.removeGift();
        return;
      }

      var declined = safeSession(function () {
        return sessionStorage.getItem(GIFT_DECLINED_KEY) === '1';
      }, false);

      if (!inCart && !declined) this.addGift(variantId);
    }

    addGift(variantId) {
      busy = true;
      cartRequest(window.routes.cart_add_url, {
        items: [{ id: variantId, quantity: 1, properties: { _free_gift: 'true' } }],
      }).finally(releaseGiftLock);
    }

    removeGift() {
      var key = this.dataset.lineKey;
      if (!key) return;

      busy = true;
      cartRequest(window.routes.cart_change_url, { id: key, quantity: 0 }).finally(function () {
        busy = false;
      });
    }

    decline() {
      safeSession(function () {
        return sessionStorage.setItem(GIFT_DECLINED_KEY, '1');
      });
      this.removeGift();
    }
  }

  if (!customElements.get('cart-gift')) customElements.define('cart-gift', CartGift);

  /* -------------------------------------------------------------------- upsell */

  class CartDrawerUpsell extends HTMLElement {
    connectedCallback() {
      var url = this.dataset.url;
      if (!url) return;

      var cached = upsellCache.get(url);
      if (cached !== undefined) {
        this.render(cached);
        return;
      }

      var self = this;
      this.fetchSection(url)
        .then(function (html) {
          if (html) return html;
          return self.dataset.fallbackUrl ? self.fetchSection(self.dataset.fallbackUrl) : null;
        })
        .then(function (html) {
          upsellCache.set(url, html);
          self.render(html);
        })
        .catch(function (e) {
          console.warn('[cart-drawer] recommendations failed', e);
        });
    }

    fetchSection(url) {
      return fetch(url)
        .then(function (response) {
          return response.text();
        })
        .then(function (text) {
          var content = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#cart-drawer-upsell-content');

          /* An empty shell means no complementary products are configured. */
          if (!content || !content.querySelector('.cd-upsell__slide')) return null;
          return content.innerHTML;
        });
    }

    render(html) {
      if (!html) return;
      this.innerHTML = html;
      this.classList.add('is-loaded');
      this.bind();
    }

    bind() {
      var self = this;

      this.querySelectorAll('[data-cart-upsell-add]').forEach(function (button) {
        button.addEventListener('click', function () {
          var variantId = parseInt(button.dataset.variantId, 10);
          if (!variantId || button.hasAttribute('aria-disabled')) return;

          button.setAttribute('aria-disabled', 'true');
          button.classList.add('is-loading');

          cartRequest(window.routes.cart_add_url, {
            items: [{ id: variantId, quantity: 1 }],
          }).finally(function () {
            /* The drawer usually re-renders this button away; restore it if it survived. */
            button.removeAttribute('aria-disabled');
            button.classList.remove('is-loading');
          });
        });
      });

      var track = this.querySelector('[data-cart-upsell-track]');
      var prev = this.querySelector('[data-cart-upsell-prev]');
      var next = this.querySelector('[data-cart-upsell-next]');
      if (!track || !prev || !next) return;

      function step(direction) {
        var slide = track.querySelector('.cd-upsell__slide');
        var amount = slide ? slide.getBoundingClientRect().width : track.clientWidth;
        track.scrollBy({ left: amount * direction, behavior: 'smooth' });
      }

      prev.addEventListener('click', function () {
        step(-1);
      });
      next.addEventListener('click', function () {
        step(1);
      });

      /* Live keeps both arrows visible at all times rather than hiding them at the ends. */
      self.classList.add('is-bound');
    }
  }

  if (!customElements.get('cart-drawer-upsell')) {
    customElements.define('cart-drawer-upsell', CartDrawerUpsell);
  }

  /* ------------------------------------------- dynamic checkout buttons */

  /*
   * The Section Rendering API cannot render content_for_additional_checkout_buttons,
   * so the markup is emitted only on the initial page render (see the full_page flag
   * in snippets/cart-drawer.liquid). Cart updates replace .drawer__inner wholesale and
   * would drop it, so hold on to the node and put it back when it disappears.
   */
  var checkoutButtons = null;

  function restoreCheckoutButtons() {
    if (!checkoutButtons) return;
    if (checkoutButtons.isConnected) return;

    var ctas = document.querySelector('cart-drawer .cart__ctas');
    if (ctas) ctas.appendChild(checkoutButtons);
  }

  function watchCheckoutButtons() {
    checkoutButtons = document.querySelector('[data-persist-checkout-buttons]');
    if (!checkoutButtons || !window.MutationObserver) return;

    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    /* Runs as a microtask after the replacement, so it lands before the next paint. */
    new MutationObserver(restoreCheckoutButtons).observe(drawer, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchCheckoutButtons);
  } else {
    watchCheckoutButtons();
  }
})();
